import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut, Target } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { useCustomer } from "@/context/CustomerContext";
import { api, formatError } from "@/lib/api";
import { addDays, prettyDate, todayISO } from "@/lib/nutri";
import { DaySummary } from "@/components/nutri/DaySummary";
import { MealList } from "@/components/nutri/MealList";
import { WaterCard } from "@/components/nutri/WaterCard";
import { WeightCard } from "@/components/nutri/WeightCard";
import { WeekSummary } from "@/components/nutri/WeekSummary";
import { GoalsDialog } from "@/components/nutri/GoalsDialog";
import { AddFoodDialog } from "@/components/nutri/AddFoodDialog";

export const NutriDashboard = () => {
  const { customer, logout } = useCustomer();
  const [date, setDate] = useState(todayISO());
  const [day, setDay] = useState(null);
  const [week, setWeek] = useState(null);
  const [addMeal, setAddMeal] = useState(null);
  const [goalsOpen, setGoalsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, w] = await Promise.all([api.get(`/nutri/day/${date}`), api.get("/nutri/week", { params: { end: date } })]);
      setDay(d.data);
      setWeek(w.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const isToday = date === todayISO();

  return (
    <main data-testid="nutri-dashboard" className="min-h-screen px-5 pb-20 pt-6 md:px-10">
      <div className="mx-auto max-w-[1300px]">
        <header className="mb-8 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nutri-home-link" className="flex items-center gap-3">
            <LogoMark size={36} />
            <span className="font-display text-2xl font-900 uppercase text-[#98bfa2]">Nutri</span>
          </Link>
          <div className="flex items-center gap-3">
            <button data-testid="open-goals-btn" onClick={() => setGoalsOpen(true)} className="flex items-center gap-2 border border-[rgba(152,191,162,0.3)] px-3 py-2 font-mono text-xs uppercase tracking-widest hover:border-[#c4dcca]">
              <Target size={14} /> <span className="hidden sm:inline">Obiettivi</span>
            </button>
            {customer?.picture && <img src={customer.picture} alt="" className="h-8 w-8 rounded-full" />}
            <button data-testid="nutri-dashboard-logout-btn" onClick={logout} aria-label="Esci" className="p-2 text-[#a3b1aa] hover:text-[#c4dcca]"><LogOut size={16} /></button>
          </div>
        </header>

        <div className="mb-8 flex items-center justify-between border-y border-[rgba(152,191,162,0.15)] py-4">
          <button data-testid="prev-day-btn" onClick={() => setDate(addDays(date, -1))} className="p-2 hover:text-[#c4dcca]" aria-label="Giorno precedente"><ChevronLeft /></button>
          <div className="text-center">
            <p data-testid="current-date-label" className="font-display text-2xl font-800 uppercase md:text-4xl">{prettyDate(date)}</p>
            {!isToday && <button data-testid="today-btn" onClick={() => setDate(todayISO())} className="font-mono text-xs uppercase tracking-widest text-[#98bfa2]">Torna a oggi</button>}
          </div>
          <button data-testid="next-day-btn" onClick={() => setDate(addDays(date, 1))} className="p-2 hover:text-[#c4dcca]" aria-label="Giorno successivo"><ChevronRight /></button>
        </div>

        {day && (
          <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
            <div className="space-y-6">
              <DaySummary totals={day.totals} goals={day.goals} />
              <MealList entries={day.entries} onAdd={setAddMeal} onChanged={load} />
            </div>
            <div className="space-y-6">
              <WaterCard date={date} ml={day.water_ml} goal={day.goals?.water_ml ?? 2000} onChanged={load} />
              <WeightCard date={date} kg={day.weight_kg} week={week} onChanged={load} />
            </div>
          </div>
        )}
        {week && <WeekSummary week={week} />}
      </div>
      <AddFoodDialog meal={addMeal} date={date} onClose={() => setAddMeal(null)} onAdded={load} />
      <GoalsDialog open={goalsOpen} onOpenChange={setGoalsOpen} goals={day?.goals} onSaved={load} />
    </main>
  );
};
